import { api } from "./api";
import { toBackendRole, type Role } from "./types/auth";

export interface RbacUser {
  id: string;
  email: string;
  name: string;
  primary_role: string;
  extra_roles: string[];
  active: boolean;
  department: string | null;
  data_scope: string | null;
  employee_no: string | null;
  phone: string | null;
  created_at: string;
  last_login_at: string | null;
  last_seen_at: string | null; // 0125 · 最后活动（登录/refresh 都刷）·在线态读它
  supervised_role_codes: string[];
  /** MFAFIX-1 · 角色是否强制 MFA（当前只有 tech_admin，见 auth/mfa.py::MFA_REQUIRED_ROLES） */
  mfa_required: boolean;
  /** MFAFIX-1 · 是否已完成绑定并启用。mfa_required=true 且此项 false = 卡在登录死锁里 */
  mfa_enabled: boolean;
}

export interface RbacRole {
  code: string;
  label_cn: string;
  label_en: string;
  permission_codes: string[];
  /** RBACROLE-1 · builtin = 8 个内置；custom = 老板 / 技术管理员在系统里新建的（code=custom_xxxxxx）*/
  kind?: "builtin" | "custom";
  description?: string | null;
  /** 主角色 + 附加角色都算 */
  user_count?: number;
  based_on_code?: string | null;
  /** 改权限后后端返 true → 前端提示已登录的同事需重新登录 */
  relogin_hint?: boolean;
}

/** 删除自定义角色被拦（409）时后端附带的在用名单 */
export interface RoleInUseUser {
  id: string;
  full_name: string;
  as: "primary" | "additional";
}

export interface RbacPermission {
  code: string;
  module: string;
  description: string | null;
  /** RBAC-ENF1 · 后端给的中文名（权限点 / 模块） */
  label_cn?: string | null;
  module_label_cn?: string | null;
  /** RBAC-UX-1 · 读写分类（后端 rbac/permissions.py 逐条标）：read 只看 / write 改数据、触发动作、审批…；旧后端没有这个字段 = undefined */
  kind?: "read" | "write" | null;
}

/** RBAC-ENF1 · 影子模式日志：按新权限点本该被拦、这次放行了的请求 */
export interface ShadowDenial {
  id: string;
  user_id: string;
  user_name: string | null;
  role_codes: string[];
  method: string;
  path: string;
  permission_code: string;
  permission_label_cn: string | null;
  created_at: string;
}

export interface ShadowDenialSummaryRow {
  role_code: string;
  permission_code: string;
  permission_label_cn: string | null;
  count: number;
  last_at: string;
}

export interface AuditLog {
  id: string;
  actor_user_id: string;
  actor_name: string | null;
  actor_role_codes: string[];
  ip_address: string | null;
  entity_type: string;
  entity_id: string;
  action: string;
  summary: string;
  before: string | null;
  after: string | null;
  occurred_at: string;
}

/**
 * 🔴 写库前把前端 Role 别名换成后端真码（`op` → `op_assistant`、
 * `account_manager` → `customer_manager`）。**createUser / updateUser 是唯二会写
 * role 的地方，收口在这里**，别散到各个页面去转换。
 */
function toBackendRolePayload<T extends Record<string, unknown>>(payload: T): T {
  const out: Record<string, unknown> = { ...payload };
  if (typeof out.primary_role === "string") out.primary_role = toBackendRole(out.primary_role);
  if (Array.isArray(out.extra_roles)) out.extra_roles = (out.extra_roles as unknown[]).map((r) => toBackendRole(String(r)));
  return out as T;
}

export const rbacApi = {
  listUsers: () => api<RbacUser[]>("/api/v1/rbac/users"),
  getUser: (uid: string) => api<RbacUser>(`/api/v1/rbac/users/${uid}`),
  createUser: (payload: {
    email: string;
    name: string;
    primary_role: Role | string;
    extra_roles?: (Role | string)[];
    active?: boolean;
    password?: string;
    department?: string | null;
    data_scope?: string | null;
    employee_no?: string | null;
    phone?: string | null;
  }) => api<RbacUser>("/api/v1/rbac/users", { method: "POST", body: JSON.stringify(toBackendRolePayload(payload)) }),
  updateUser: (
    uid: string,
    payload: {
      name?: string;
      primary_role?: Role | string;
      extra_roles?: (Role | string)[];
      active?: boolean;
      department?: string | null;
      data_scope?: string | null;
      employee_no?: string | null;
      phone?: string | null;
    },
  ) =>
    api<RbacUser>(`/api/v1/rbac/users/${uid}`, {
      method: "PATCH",
      body: JSON.stringify(toBackendRolePayload(payload)),
    }),
  deleteUser: (uid: string) => api<void>(`/api/v1/rbac/users/${uid}`, { method: "DELETE" }),
  resetPassword: (uid: string, password?: string) =>
    api<{ password: string }>(`/api/v1/rbac/users/${uid}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ password: password ?? null }),
    }),
  /** MFAFIX-1 Part 3 · 清空 mfa_secret + mfa_enabled，该账号下次登录回到绑定流程。 */
  resetMfa: (uid: string) =>
    api<RbacUser>(`/api/v1/rbac/users/${uid}/reset-mfa`, { method: "POST" }),

  listRoles: () => api<RbacRole[]>("/api/v1/rbac/roles"),
  /** RBACROLE-1 · 新建自定义角色（copy_from_code=null 为空白）*/
  createRole: (payload: { label_cn: string; description?: string; copy_from_code: string | null }) =>
    api<RbacRole>("/api/v1/rbac/roles", { method: "POST", body: JSON.stringify(payload) }),
  /** RBACROLE-1 · 改名 / 改说明（内置角色 409）*/
  renameRole: (code: string, payload: { label_cn?: string; description?: string }) =>
    api<RbacRole>(`/api/v1/rbac/roles/${code}`, { method: "PATCH", body: JSON.stringify(payload) }),
  /** RBACROLE-1 · 删除自定义角色；有人在用 → ApiError(409).detail = { detail, users: RoleInUseUser[] } */
  deleteRole: (code: string) => api<void>(`/api/v1/rbac/roles/${code}`, { method: "DELETE" }),
  listPermissions: () => api<RbacPermission[]>("/api/v1/rbac/permissions"),
  /** 改某内置角色的权限集合（V1·真生效）→ 返回更新后的角色 */
  updateRolePermissions: (code: string, permission_codes: string[]) =>
    api<RbacRole>(`/api/v1/rbac/roles/${code}/permissions`, {
      method: "PATCH",
      body: JSON.stringify({ permission_codes }),
    }),

  /** RBAC-ENF1 · 影子日志（仅老板 / 技术管理员）*/
  shadowDenials: (params?: { role?: string; permission?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.role) qs.set("role", params.role);
    if (params?.permission) qs.set("permission", params.permission);
    if (params?.limit) qs.set("limit", String(params.limit));
    const s = qs.toString();
    return api<ShadowDenial[]>(`/api/v1/rbac/shadow-denials${s ? `?${s}` : ""}`);
  },
  shadowDenialsSummary: () => api<ShadowDenialSummaryRow[]>("/api/v1/rbac/shadow-denials/summary"),

  auditLogs: (params?: { entity_type?: string; entity_id?: string; actor?: string; action?: string }) => {
    const qs = new URLSearchParams();
    if (params?.entity_type) qs.set("entity_type", params.entity_type);
    if (params?.entity_id) qs.set("entity_id", params.entity_id);
    if (params?.actor) qs.set("actor", params.actor);
    if (params?.action) qs.set("action", params.action);
    const s = qs.toString();
    return api<AuditLog[]>(`/api/v1/rbac/audit-logs${s ? `?${s}` : ""}`);
  },

  grantSupervision: (uid: string, supervised_role_code: Role) =>
    api<RbacUser>(`/api/v1/rbac/users/${uid}/grant-supervision`, {
      method: "POST",
      body: JSON.stringify({ supervised_role_code }),
    }),
  revokeSupervision: (uid: string, sid: string) =>
    api<RbacUser>(`/api/v1/rbac/users/${uid}/grant-supervision/${sid}`, { method: "DELETE" }),
};
